import { CARD_PATH_SEGMENT } from 'common/constants/url-params';
import { prependUrlProtocol } from 'utils/url/prepend-url-protocol';

/**
 * Path prefix for proxy/custom hosts (e.g. /happy-customer/…).
 * Prod: window.STORY_PATH_PREFIXES + longest pathname boundary match.
 */

let cached: string | undefined;

/** Canonical form: leading `/`, no trailing `/`. Empty when blank. */
export function canonicalizePathPrefix(raw: string | null | undefined): string {
	if (raw == null) return '';
	let value = String(raw).trim();
	if (!value) return '';
	if (!value.startsWith('/')) value = `/${value}`;
	if (value.length > 1) value = value.replace(/\/+$/, '');
	return value;
}

/** First non-empty canonical prefix for admin publish/embed URL building. */
export function pickPublishPathPrefix(pathPrefixes?: string[] | null): string {
	if (!pathPrefixes?.length) return '';
	for (const raw of pathPrefixes) {
		const prefix = canonicalizePathPrefix(raw);
		if (prefix) return prefix;
	}
	return '';
}

/** Longest prefix where pathname === prefix or pathname.startsWith(prefix + '/'). */
export function matchPathPrefix(pathname: string, prefixes: string[] | null | undefined): string {
	if (!prefixes?.length) return '';

	let best = '';
	for (const raw of prefixes) {
		const prefix = canonicalizePathPrefix(raw);
		if (prefix && (pathname === prefix || pathname.startsWith(`${prefix}/`))) {
			if (prefix.length > best.length) best = prefix;
		}
	}
	return best;
}

function resolvePrefixCandidates(): string[] {
	const fromWindow = window.STORY_PATH_PREFIXES;
	return Array.isArray(fromWindow) ? fromWindow : [];
}

/**
 * Active path prefix for URL building, or '' when prefix logic is off.
 * Candidates from window.STORY_PATH_PREFIXES; then pathname match.
 */
export function getActivePathPrefix(): string {
	if (cached !== undefined) return cached;

	const candidates = resolvePrefixCandidates();
	const pathname = window.location.pathname || '/';
	cached = matchPathPrefix(pathname, candidates);

	// eslint-disable-next-line no-console
	console.info('[path-prefix]', {
		prefix: cached || '(none)',
		candidates,
		pathname,
		href: window.location.href,
	});

	return cached;
}

/** Story path segment after custom-domain strip (no leading slash). */
export function buildStorySegment({
	hostname,
	storiesDomain,
	storyBasePath,
}: {
	hostname: string;
	storiesDomain: string | undefined;
	storyBasePath: string | undefined;
}): string {
	if (hostname === storiesDomain) {
		return storyBasePath || '';
	}
	if (typeof storyBasePath === 'string') {
		return storyBasePath.split('/')[1] || '';
	}
	return '';
}

/** Published story root pathname, e.g. `/happy-customer/rKfKlH2nD`. */
export function buildStoryPathBase({ storySegment, pathPrefix }: { storySegment: string; pathPrefix: string }): string {
	if (!storySegment) return pathPrefix || '/';
	return pathPrefix ? `${pathPrefix}/${storySegment}` : `/${storySegment}`;
}

export function buildCardPathname({ storyPathBase, cardId }: { storyPathBase: string; cardId: string }): string {
	const base = storyPathBase.replace(/\/+$/, '');
	return `${base}/${CARD_PATH_SEGMENT}/${cardId}/`;
}

export function buildWebpackPublicPath({
	origin,
	storySegment,
	pathPrefix,
}: {
	origin: string;
	storySegment: string;
	pathPrefix: string;
}): string {
	const path = buildStoryPathBase({ storySegment, pathPrefix });
	return `${origin}${path.endsWith('/') ? path : `${path}/`}`;
}

/** Same-origin API base: swap hostname and optionally insert path prefix before pathname. */
export function buildClientBackendUrl({
	configuredApiUrl,
	hostname,
	pathPrefix,
}: {
	configuredApiUrl: string;
	hostname: string;
	pathPrefix: string;
}): string {
	try {
		const url = new URL(prependUrlProtocol(configuredApiUrl, 'https'));
		const pathname = url.pathname === '/' ? '' : url.pathname;
		return `${url.protocol}//${hostname}${pathPrefix}${pathname}`;
	} catch {
		return configuredApiUrl;
	}
}
